import { api, usuarioEhRoot, upsertSavedAccount, AccountVaultFullError } from "roboflex-thalamus-request-handler";
import { cookies } from "roboflex-thalamus-utils-lib";
import { sso } from "roboflex-thalamus-sso-lib";
import authService, { domainCookieSessao, gravarCookieSessao, segundosRestantesToken } from "@/services/authService";

const SSO_COOKIE_NAME = "thalamusSession";
const COOKIE_PATH = "/";
const ME_CACHE_TTL_MS = 5000;

const MARGEM_RENOVACAO_SEG = 300;

let meRequestEmAndamento = null;
let ultimoEnriquecimentoMe = 0;
let renovacaoEmAndamento = null;
let geracaoSessao = 0;

function claimsDoToken(token) {
  if (!token || typeof token !== "string") return {};
  const parts = token.split(".");
  if (parts.length < 2) return {};
  try {
    let payloadB64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = payloadB64.length % 4;
    if (pad) payloadB64 += "=".repeat(4 - pad);
    return JSON.parse(atob(payloadB64)) || {};
  } catch {
    return {};
  }
}

export function persistirUsuarioSessao(usuario) {
  gravarCookieSessao(usuario);
  return usuario;
}

/**
 * Persiste sessão ativa e registra/atualiza no cofre multi-conta (não limpa outras contas).
 * @returns {{ usuario: object, vaultError: Error|null }}
 */
export function registrarSessaoNoCofre(usuario) {
  persistirUsuarioSessao(usuario);
  try {
    upsertSavedAccount(usuario);
    return { usuario, vaultError: null };
  } catch (e) {
    if (e instanceof AccountVaultFullError || e?.name === "AccountVaultFullError") {
      return { usuario, vaultError: e };
    }
    throw e;
  }
}

/**
 * Remove thalamusSession no mesmo formato em que o portal grava (host-only e com domain).
 * O sso.logout() da lib só apaga com domain=thalamus.ind.br — falha em localhost.
 */
export function limparSessao() {
  geracaoSessao += 1;
  meRequestEmAndamento = null;
  renovacaoEmAndamento = null;
  ultimoEnriquecimentoMe = 0;

  if (typeof document === "undefined") return;

  const expirado = "Thu, 01 Jan 1970 00:00:00 GMT";
  const base = `${SSO_COOKIE_NAME}=; path=${COOKIE_PATH}; expires=${expirado}; max-age=0; SameSite=Lax`;

  // Cookie host-only (login local / development)
  document.cookie = base;

  const domain = domainCookieSessao();
  if (domain) {
    document.cookie = `${base}; domain=${domain}`;
  }

  // Fallback do domínio legado usado pela lib SSO
  document.cookie = `${base}; domain=thalamus.ind.br`;

  try {
    cookies.setCookie(SSO_COOKIE_NAME, "", -1);
  } catch {
    // ignore
  }
}

/**
 * Renova o JWT quando falta pouco tempo (ou já expirou, dentro da janela de refresh).
 * Falha com token já vencido limpa a sessão e propaga o erro.
 */
export async function renovarSessaoSeNecessario() {
  const atual = sso.getUsuarioLogado();
  if (!atual?.token) return atual;

  const segundos = segundosRestantesToken(atual.token);
  if (segundos == null || segundos > MARGEM_RENOVACAO_SEG) return atual;

  if (renovacaoEmAndamento) return renovacaoEmAndamento;

  const geracao = geracaoSessao;
  renovacaoEmAndamento = (async () => {
    try {
      const payload = await authService.renovarToken(atual.token);
      if (geracao !== geracaoSessao) return sso.getUsuarioLogado();

      const user = payload?.user || {};
      const usuarioLogado = {
        ...atual,
        id: user.id ?? atual.id,
        nome: user.name ?? atual.nome,
        email: user.email ?? atual.email ?? null,
        token: payload.access_token,
        tokenType: payload.token_type || atual.tokenType || "Bearer",
        empresa_id: user.empresa_id ?? atual.empresa_id ?? null,
        is_root: user.is_root != null ? !!user.is_root : !!atual.is_root,
      };
      registrarSessaoNoCofre(usuarioLogado);
      ultimoEnriquecimentoMe = 0;
      return usuarioLogado;
    } catch (error) {
      const restante = segundosRestantesToken(atual.token);
      if (restante != null && restante <= 0) {
        limparSessao();
        throw error;
      }
      return atual;
    } finally {
      renovacaoEmAndamento = null;
    }
  })();

  return renovacaoEmAndamento;
}

/**
 * Consulta /auth/me e mescla is_root / empresa_id na sessão SSO (cookie compartilhado entre apps).
 * Específico do portal — a lib só atualiza cache em memória.
 */
export async function enriquecerSessaoComMe() {
  let atual = await renovarSessaoSeNecessario();
  if (!atual?.token) return atual;

  const agora = Date.now();
  const jaTemContextoBasico =
    typeof atual.is_root !== "undefined" || atual.empresa_id != null;
  if (jaTemContextoBasico && agora - ultimoEnriquecimentoMe < ME_CACHE_TTL_MS) {
    return atual;
  }

  if (meRequestEmAndamento) {
    return meRequestEmAndamento;
  }

  meRequestEmAndamento = (async () => {
    try {
      const { data } = await api.get("auth/me");
      const atualizado = {
        ...atual,
        id: data.id ?? atual.id,
        nome: data.name ?? atual.nome,
        email: data.email ?? atual.email ?? null,
        empresa_id: data.empresa_id ?? null,
        empresa_nome: data.empresa_nome ?? null,
        is_root: !!data.is_root,
      };
      registrarSessaoNoCofre(atualizado);
      ultimoEnriquecimentoMe = Date.now();
      return atualizado;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        limparSessao();
        throw error;
      }
      const claims = claimsDoToken(atual.token);
      if (typeof claims.is_root !== "undefined" || typeof claims.empresa_id !== "undefined") {
        const atualizado = {
          ...atual,
          empresa_id: claims.empresa_id ?? atual.empresa_id ?? null,
          is_root: !!claims.is_root,
        };
        registrarSessaoNoCofre(atualizado);
        ultimoEnriquecimentoMe = Date.now();
        return atualizado;
      }
      return atual;
    } finally {
      meRequestEmAndamento = null;
    }
  })();

  return meRequestEmAndamento;
}

export function rotaInicialDoUsuario(usuario) {
  return usuarioEhRoot(usuario) ? { name: "painelRoot" } : { name: "home" };
}

/** Lê add_account / return_url da rota ou do hash (compat). */
export function paramsLoginMultiConta(route) {
  const fromRoute = {
    addAccount: String(route?.query?.add_account || "") === "1",
    returnUrl: route?.query?.return_url ? String(route.query.return_url) : "",
  };
  if (fromRoute.addAccount || fromRoute.returnUrl) {
    return fromRoute;
  }
  if (typeof window === "undefined") {
    return { addAccount: false, returnUrl: "" };
  }
  try {
    const hash = window.location.hash || "";
    const qIdx = hash.indexOf("?");
    if (qIdx < 0) return { addAccount: false, returnUrl: "" };
    const qs = new URLSearchParams(hash.slice(qIdx + 1));
    return {
      addAccount: qs.get("add_account") === "1",
      returnUrl: qs.get("return_url") || "",
    };
  } catch {
    return { addAccount: false, returnUrl: "" };
  }
}

function returnUrlPermitida(url) {
  if (!url) return false;
  try {
    const u = new URL(url, window.location.origin);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    const host = u.hostname;
    if (host === "localhost" || host === "127.0.0.1") return true;
    return host === "thalamus.ind.br" || host.endsWith(".thalamus.ind.br");
  } catch {
    return false;
  }
}

/**
 * Após login: se add_account + return_url válida, volta ao módulo; senão rota inicial do portal.
 */
export function redirecionarAposLogin(usuario, router, route) {
  const { addAccount, returnUrl } = paramsLoginMultiConta(route);
  if (addAccount && returnUrlPermitida(returnUrl)) {
    window.location.href = returnUrl;
    return;
  }
  router.push(rotaInicialDoUsuario(usuario));
}
